import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eamico-core/artifacts/uowh0b6l_Untitled%20design.png";

const Dropdown = ({ label, items, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-4 py-2 text-[14px] font-medium text-[#5f6368] hover:text-[#202124] rounded-full hover:bg-[#f1f3f4] transition-colors duration-150"
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] border border-[#e8eaed] py-2 z-50">
          {items.map((item) => (
            item.disabled ? (
              <div
                key={item.label}
                className="flex items-start gap-3 px-4 py-2.5 cursor-default"
                data-testid={`dropdown-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.icon && (
                  <span className="mt-0.5 text-[#d2d4d8]">{item.icon}</span>
                )}
                <div>
                  <div className="text-[14px] font-medium text-[#c4c7cc]">{item.label}</div>
                  {item.desc && (
                    <div className="text-[12px] text-[#d2d4d8] mt-0.5">{item.desc}</div>
                  )}
                </div>
              </div>
            ) : (
              <Link
                key={item.label}
                to={item.href}
                className="flex items-start gap-3 px-4 py-2.5 hover:bg-[#f8f9fa] transition-colors"
                onClick={() => setOpen(false)}
                data-testid={`dropdown-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {item.icon && (
                  <span className="mt-0.5 text-[#ff9a4d]">{item.icon}</span>
                )}
                <div>
                  <div className="text-[14px] font-medium text-[#202124]">{item.label}</div>
                  {item.desc && (
                    <div className="text-[12px] text-[#5f6368] mt-0.5">{item.desc}</div>
                  )}
                </div>
              </Link>
            )
          ))}
        </div>
      )}
    </div>
  );
};

const Navigation = ({ onOpenWaitlist }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const productItems = [
    {
      label: "Eamico Cloud",
      href: "/cloud",
      desc: "The world's fastest edge infrastructure — own it, run it, earn from it.",
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg>,
    },
  ];

  const moreItems = [
    { label: "Enterprise", disabled: true },
    { label: "Careers", disabled: true },
    { label: "Community", disabled: true },
    { label: "Changelog", disabled: true },
  ];

  const directLinks = [
    { label: "About", href: "/about" },
    { label: "Docs", href: "/docs" },
    { label: "Blog", href: "/blog" },
  ];

  const allMobileLinks = [
    { label: "Eamico Cloud", href: "/cloud" },
    ...directLinks,
    ...moreItems,
  ];

  return (
    <>
    <div className="fixed top-0 left-0 right-0 z-[51] flex items-center justify-center gap-0 text-[12px] font-medium tracking-wide" data-testid="dev-banner">
      <div className="flex-1 bg-[#ff9a4d] text-white text-center py-1.5">Site under development. Coming Soon</div>
    </div>
    <nav
      className="fixed top-[30px] left-0 right-0 z-50 bg-white shadow-[0_1px_6px_rgba(0,0,0,0.1)]"
      data-testid="navigation"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Eamico" className="h-9 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-0.5">
          <Dropdown label="Products" items={productItems} testId="nav-products-dropdown" />
          {directLinks.map((link) => (
            link.disabled ? (
              <span
                key={link.label}
                className="px-4 py-2 text-[14px] font-medium text-[#c4c7cc] cursor-default"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </span>
            ) : link.external ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-[14px] font-medium text-[#5f6368] hover:text-[#202124] rounded-full hover:bg-[#f1f3f4] transition-colors duration-150"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="px-4 py-2 text-[14px] font-medium text-[#5f6368] hover:text-[#202124] rounded-full hover:bg-[#f1f3f4] transition-colors duration-150"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            )
          ))}
          <Dropdown label="More" items={moreItems} testId="nav-more-dropdown" />
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={onOpenWaitlist}
            className="text-[14px] font-medium text-[#ff9a4d] hover:text-[#e8872e] px-3 py-2 rounded-full hover:bg-[#fff4eb] transition-colors duration-150"
            data-testid="nav-contact-link"
          >
            Contact us
          </button>
          <button
            onClick={onOpenWaitlist}
            className="text-[14px] font-medium text-white bg-[#ff9a4d] hover:bg-[#e8872e] px-5 py-2.5 rounded-md transition-colors duration-150 shadow-sm"
            data-testid="nav-signup-button"
          >
            Get started
          </button>
        </div>

        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#f1f3f4] transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-button"
          aria-label="Menu"
        >
          {mobileOpen ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          )}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#e8eaed] px-6 py-4 space-y-1 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          {allMobileLinks.map((link) => (
            link.disabled ? (
              <span
                key={link.label}
                className="block px-3 py-2.5 text-[15px] font-medium text-[#c4c7cc] cursor-default"
              >
                {link.label}
              </span>
            ) : link.external ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-2.5 text-[15px] font-medium text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] rounded-lg transition-colors"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="block px-3 py-2.5 text-[15px] font-medium text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] rounded-lg transition-colors"
              >
                {link.label}
              </Link>
            )
          ))}
          <div className="pt-3 border-t border-[#e8eaed] mt-2 flex flex-col gap-2">
            <button onClick={() => { setMobileOpen(false); onOpenWaitlist?.(); }} className="text-left text-[14px] font-medium text-[#ff9a4d] px-3 py-2">Contact us</button>
            <button onClick={() => { setMobileOpen(false); onOpenWaitlist?.(); }} className="text-center text-[14px] font-medium text-white bg-[#ff9a4d] px-5 py-2.5 rounded-md">Get started</button>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navigation;
